import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { PRESETS_BIOMETRICS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import BiometricDevicePresetColumns from "../models/ColumnDefinitions/BiometricDevicePresetColumns";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/HardwarePresetSearch';
import SiteSearch from "../models/search/SiteSearch";
import { deletePreset, getBiometricDevicePreset } from "../services/hardwarePresetService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void
}

const BiometricPresetsTab: FC<Props> = ({ setBusy }) => {
    const { translate } = useLocale()
    const title = translate.byKey(TranslationKeys.allBiometricPresets);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/hardware/biometric-preset/';

    useEffect(() => {
        (async () => {
            setLookup([{
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            }]);
        })();
    }, [])

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, DEVICE_TIME_PATTERNS) => void) => {
        setDetails(userLoginId, PRESETS_BIOMETRICS);
    }

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deletePreset(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                    },
                    deleteResult => {
                        reject(deleteResult);
                    }).finally(() => {
                        setBusy(false);
                    })
        });
    }

    // <-- end context

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getBiometricDevicePreset}
        tableDescriptor={BiometricDevicePresetColumns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        lookup={lookup}
        deleteMethod={onDelete}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }}
        >
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath} 
            cyName="biometricPresets"/>
    </TableContextProvider>
}

export default BiometricPresetsTab;
